import { FileTextOutlined, PushpinFilled, PushpinOutlined, TeamOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { Button, Divider, Popover } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import {
  GetViewerFollowingGroupsDocument,
  GroupPrivateSetting,
  useGetGroupCardInfoQuery,
  useGetViewerQuery,
  useGroupPinUpdateMutation,
} from '@/generated/graphql'
import { Follow } from '@/modules/follow'
import { routes } from '@/routes'

import { GroupAvatar } from '../avatar/avatar.component'
import s from './group-hover-card.module.less'

const Content: React.FC<{ groupId: number }> = ({ groupId }) => {
  const { t } = useTranslation()
  const { data } = useGetGroupCardInfoQuery({
    fetchPolicy: 'cache-only',
    variables: {
      groupId,
    },
  })
  const { data: viewerData } = useGetViewerQuery()

  const [groupPinUpdate] = useGroupPinUpdateMutation({
    refetchQueries: [getOperationName(GetViewerFollowingGroupsDocument)!],
  })

  if (!data || !viewerData) {
    return null
  }

  const group = data.group
  const isPrivate = group.privateSetting === GroupPrivateSetting.Closed
  const isCreator = viewerData?.viewer.userId === group.creatorUserId

  const quickAccessButton = group.isFollowing && (
    <Button
      onClick={() => {
        groupPinUpdate({ variables: { groupId: group.groupId } })
      }}
      icon={group.isPinned ? <PushpinFilled /> : <PushpinOutlined />}
    ></Button>
  )

  return (
    <div className={s.card}>
      <div className={s.top}>
        <GroupAvatar style={{ fontSize: 38 }} groupCatId={group.category?.groupCategoryId} />
        {quickAccessButton}
        {!group.isFollowing && (
          <Follow
            {...(group.isFollowing && { disabled: !group.canUnfollow || isCreator })}
            isPrivate={isPrivate}
            followeeGroupId={group.groupId}
            isFollowing={group.isFollowing}
            display='text'
          />
        )}
      </div>
      <div className={s.primaryInfo}>
        <span style={{ fontWeight: 'bold' }}>{group.name}</span>
      </div>
      <div className={s.wrapper}>
        <div>
          <span>{t('group.creator')}:</span>
          <span>
            <Link to={routes.users.page(group.creator.userId)}>{group.creator.name}</Link>
          </span>
        </div>
        <div>
          <span>{t('group.info')}:</span>
          <span className={s.description}>{group.description && group.description}</span>
        </div>
      </div>
      <Divider style={{ margin: '8px 0' }} />
      <div className={s.groupInfo}>
        <div>
          <TeamOutlined />
          <span>{group.followers.totalCount}</span>
        </div>
        <div>
          <FileTextOutlined />
          <span>{group.posts.totalCount}</span>
        </div>
      </div>
    </div>
  )
}

export const GroupHoverCard: React.FC<{ groupId: number }> = ({ groupId, children }) => {
  const [visible, setVisible] = React.useState(false)
  const { data } = useGetGroupCardInfoQuery({
    skip: !visible,
    variables: {
      groupId,
    },
  })

  return (
    <Popover
      destroyTooltipOnHide
      visible={!!data}
      onVisibleChange={(visible) => setVisible(visible)}
      trigger='hover'
      placement='top'
      content={<Content groupId={groupId} />}
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
    >
      {children}
    </Popover>
  )
}
